/**
 * Cache-busting URLs for the WASM binaries served out of public/.
 *
 * The token is the file's own content hash, computed at build time by
 * config-overrides.js and injected as __WASM_TOKENS__. Before this, each
 * call site carried a hand-written marker — '?v=nu63', '?v=ironwood' —
 * that had to be bumped in a separate commit from the rebuild.
 *
 * That step was forgotten, exactly as you would expect. The keys-and-send
 * signer was rebuilt for Ironwood on 29 July 2026 and the marker in
 * webzjs-keys-and-send-client-minimal.ts still read 'nu63' three days
 * later. A browser holding the previous binary under that URL keeps using
 * it, and a stale signer builds transactions against the wrong consensus
 * branch id — the network rejects them, and the user sees a send that
 * fails for no reason they can act on.
 *
 * Hashing the bytes removes the human step: the URL changes if and only if
 * the binary changes.
 */

const TOKENS = typeof __WASM_TOKENS__ === 'object' && __WASM_TOKENS__ ? __WASM_TOKENS__ : {};

/**
 * Public URL for a WASM file with its content-hash cache token.
 *
 * @param {string} file path under public/, e.g. 'webzjs_keys_and_send_bg.wasm'
 * @returns {string} '/file?v=<hash>' — or '/file' when the hash is unknown
 *   (jest, or a binary generated after this build), because an unversioned
 *   URL is merely cacheable while a wrong one is actively misleading.
 */
export const wasmUrl = (file) => {
	const token = TOKENS[file];
	return token ? `/${file}?v=${token}` : `/${file}`;
};

/** The token itself, for logging and for the entropy/consistency reports. */
export const wasmToken = (file) => TOKENS[file] || null;

/** Every token this build knows about, for a diagnostics panel. */
export const allWasmTokens = () => ({ ...TOKENS });
